import { defineStore } from 'pinia'
import { service } from './service.js';
import constants from '../constants/generic.js';
import opmGeneric from '../utils/generic.js';

const getDefaultState = () => ({
    loanEnquiryConfig: {},
    loanEnquiryData: {},
    elsConfig: {
        lstLabel: {},
        lstInUse: {},
        lstRequired: {},
        lstHelperText: {},
    },
    paymentConfirmed: false,
  });
  
export const useLoanEnquiryStore = defineStore('LoanEnquiryStore', {
    state: getDefaultState,
    persist: true,
    getters: {
        referenceData: state => { return state.loanEnquiryConfig.ReferenceData; },
        elementsConfig: state => { return state.elsConfig; },
    },
    actions: {
        async loadLoanEnquiryConfig() {
            // When refreshing the app the configuration is already loaded, no need to load it again
            if (Object.keys(this.loanEnquiryConfig).length != 0) return { LoanEnquiryConfig: this.loanEnquiryConfig };

            const config = await service.getLoanEnquiryConfig();
            if (config?.Response?.StatusCode !== 0) {
                return config;
            }
            this.loanEnquiryConfig = config.LoanEnquiryConfig;
            // Parse field configuration for "Child.Components"
            if (config.LoanEnquiryConfig.FieldsConfiguration) {
                const isPrimaryLang = opmGeneric.isPrimaryLang();
                config.LoanEnquiryConfig.FieldsConfiguration.forEach(el => {
                    this.elsConfig.lstInUse[el.Name] = true; // Only InUse fields are returned
                    this.elsConfig.lstRequired[el.Name] = el.Required;
                    this.elsConfig.lstLabel[el.Name] = isPrimaryLang ? el.Label : el.LabelLang2;
            
                    // HelperText support for "Param.*"
                    let text = isPrimaryLang ? el.HelperText : el.HelperTextLang2;
                    if (text === null || text === undefined || text === '') return '';
                    if (text.indexOf("{CUInfo.") !== -1) Object.entries(this.loanEnquiryConfig.CreditUnionInfo).forEach(p => text = text.replaceAll("{CUInfo.".concat(p[0], "}"), p[1]));
                    if (text.indexOf("{Param.") !== -1) this.loanEnquiryConfig.Params.forEach(p => text = text.replaceAll("{Param.".concat(p.Name, "}"), p.Value));
                    if (text.indexOf("{Setting.") !== -1) this.loanEnquiryConfig.ReferenceData.SystemSettings.forEach(p => text = text.replaceAll("{Setting.".concat(p.Name, "}"), p.Value));
                    this.elsConfig.lstHelperText[el.Name] = text;
                });
            }
            
            return config;
        },

        updateLocalLoanEnquiryData(payload) {
            this.loanEnquiryData = Object.assign(this.loanEnquiryData, payload);
        },

        updateLoanEnquiryConfig(payload) {
            this.loanEnquiryConfig = Object.assign(this.loanEnquiryConfig, payload);
        },

        async saveLoanEnquiry(payload) {
            this.updateLocalLoanEnquiryData(payload);
            const resp = await service.saveLoanEnquiry(this.loanEnquiryData);
            if (resp.ReturnedErrorCode === constants.LOAN_ENQUIRY_SUCCESS) {
              // Store DocPack in state
              this.loanEnquiryConfig = Object.assign(this.loanEnquiryConfig, { 
                AccessToken: resp.AccessToken,
                DocPack: resp.MemAppDocumentPackDetailList, 
                DocumentPackComplete: resp.DocumentPackComplete,
                EncodedQueryString: resp.EncodedQueryString,
                IdentityVerificationStatus: resp.IdentityVerificationStatus, 
                MembershipApplicationStatus_Status: resp.MembershipApplicationStatus_Status,
                Consent: resp.Consent,
                ConsentText: resp.ConsentText,
                ConsentTextID: resp.ConsentTextID
             });

             this.loanEnquiryData.AccessToken = resp.AccessToken;
            }

            return resp;
        },

        async checkEligibility(payload) {
            this.updateLocalLoanEnquiryData(payload);
            return await service.checkEligibility(payload);
        },

        async otpValidate(payload) {
            this.updateLocalLoanEnquiryData(payload);
            const resp = await service.otpValidate(payload);
            if (resp.Response.StatusCode === constants.LOAN_ENQUIRY_SUCCESS) {
                // Store Token.TokenId in state
                this.updateLocalLoanEnquiryData({ AccessToken : resp.Token.TokenId });

                this.loanEnquiryConfig = Object.assign(this.loanEnquiryConfig, { 
                    AccessToken: resp.Token.TokenId,
                    DocPack: resp.LoanEnquiry.MemAppDocumentPackDetailList, 
                    DocumentPackComplete: resp.LoanEnquiry.DocumentPackComplete,
                    EncodedQueryString: resp.LoanEnquiry.EncodedQueryString,
                    IdentityVerificationStatus: resp.LoanEnquiry.IdentityVerificationStatus, 
                    MembershipApplicationStatus_Status: resp.LoanEnquiry.MembershipApplicationStatus_Status,
                    Consent: resp.LoanEnquiry.Consent,
                    ConsentText: resp.LoanEnquiry.ConsentText,
                    ConsentTextID: resp.LoanEnquiry.ConsentTextID
                 });
            }
            return resp;
        },

        async requestOpen(payload) {
            this.updateLocalLoanEnquiryData(payload);
            const resp = await service.requestOpen(payload);
            
            return resp;
        },

        async openEnquiry(payload) {            
            this.updateLocalLoanEnquiryData(payload);
            const resp = await service.openEnquiry(payload);
            
            this.loanEnquiryConfig = Object.assign(this.loanEnquiryConfig, { DocPack: resp.MemAppDocumentPackDetailList, 
                                                                            IdentityVerificationStatus: resp.IdentityVerificationStatus, 
                                                                            DocumentPackComplete: resp.DocumentPackComplete, 
                                                                            MembershipApplicationStatus_Status: resp.MembershipApplicationStatus_Status });
                
            this.loanEnquiryData.AccessToken = resp.AccessToken;
            
            return resp;
        },

        async enquiryDoc(payload) {
            const body = {
                EncodedQueryString: payload.EncodedQueryString,
                AccessToken: this.loanEnquiryData.AccessToken,
                DocumentTypeID: payload.docPackInfo.DocumentType_ID,
                DocumentPackDetailID: payload.docPackInfo.MemberDocumentPackDetailID,
                Documents: payload.docPackFiles
            };
            
            return await service.enquiryDoc(body);
        },

        async enquiryIdVerification(payload) {        
            const body = {
                EncodedQueryString: payload.EncodedQueryString,
                AccessToken: this.loanEnquiryData.AccessToken,
                ConsentBiometric: payload.ConsentBiometric,
                ConsentPhoto: payload.ConsentPhoto,
                ConsentTextID: payload.ConsentTextID,
                RequestBody: {
                    "CustomerInternalReference": "",
                    "WorkflowDefinition": {
                        "Key": "",
                        "Credentials": [
                            {
                                "Category": "ID",
                                "Type": {
                                    "PredefinedType": "DEFINED",
                                    "Values": ["PASSPORT", "DRIVING_LICENSE", "ID_CARD"]
                                }
                            }
                        ]
                    },
                    "ReportingCriteria": "",
                    "CallbackUrl": "",
                    "UserReference": ""
                }
            };
            
            return await service.enquiryIdVerification(body);
        },

        confirmPayment() {
            this.paymentConfirmed = true;
        },
        
        resetState () {
            // Merge rather than replace so we don't lose observers
            // https://github.com/vuejs/vuex/issues/1118
            Object.assign(this, getDefaultState());
        },
    }
})